import React, { useEffect, useRef, useState } from 'react'

// MaskOverlay must be rendered INSIDE the image container it should cover
const MaskOverlay = ({ visible, containerRef, onApply, onClose }) => {
  const canvasRef = useRef(null)
  const boundaryCanvasRef = useRef(null)
  const [brushSize, setBrushSize] = useState(16)
  const [painting, setPainting] = useState(false)
  const [isSliding, setIsSliding] = useState(false)
  
  // Undo/Redo functionality
  const [maskHistory, setMaskHistory] = useState([])
  const [historyIndex, setHistoryIndex] = useState(-1)
  const [canUndo, setCanUndo] = useState(false)
  const [canRedo, setCanRedo] = useState(false)

  useEffect(() => {
    const syncSize = () => {
      const canvas = canvasRef.current
      const boundaryCanvas = boundaryCanvasRef.current
      const host = containerRef?.current
      if (!canvas || !boundaryCanvas || !host) return
      
      // Get the actual image element
      const img = host.querySelector('.main-image')
      if (!img) return
      
      // Wait for image to load to get accurate dimensions
      if (!img.complete) {
        img.onload = () => syncSize()
        return
      }
      
      const { clientWidth, clientHeight } = host
      canvas.width = Math.max(1, Math.floor(clientWidth))
      canvas.height = Math.max(1, Math.floor(clientHeight))
      boundaryCanvas.width = Math.max(1, Math.floor(clientWidth))
      boundaryCanvas.height = Math.max(1, Math.floor(clientHeight))
      
      // Calculate exact image bounds within the container
      calculateImageBounds(canvas, img, host)
      
      // Initialize mask history with empty canvas
      if (maskHistory.length === 0) {
        const emptyCanvas = document.createElement('canvas')
        emptyCanvas.width = canvas.width
        emptyCanvas.height = canvas.height
        const emptyCtx = emptyCanvas.getContext('2d')
        emptyCtx.clearRect(0, 0, canvas.width, canvas.height)
        
        setMaskHistory([emptyCanvas])
        setHistoryIndex(0)
        setCanUndo(false)
        setCanRedo(false)
      }
    }
    syncSize()
    window.addEventListener('resize', syncSize)
    return () => window.removeEventListener('resize', syncSize)
  }, [visible, containerRef])

  const calculateImageBounds = (canvas, img, container) => {
    const naturalWidth = img.naturalWidth
    const naturalHeight = img.naturalHeight
    
    // Find the actual image container (main-image-container), not the larger container
    const imageContainer = img.closest('.main-image-container')
    if (!imageContainer) {
      console.error('❌ Could not find main-image-container')
      return
    }
    
    // Get the image container dimensions (this is the actual image area)
    const containerWidth = imageContainer.clientWidth
    const containerHeight = imageContainer.clientHeight
    
    // Get the image's actual CSS dimensions (what's actually displayed)
    const imgWidth = img.clientWidth
    const imgHeight = img.clientHeight
    
    // Calculate how object-fit: contain positions the image
    const imgAspect = naturalWidth / naturalHeight
    const containerAspect = containerWidth / containerHeight
    
    let offsetX, offsetY
    
    if (imgAspect > containerAspect) {
      // Image is wider - fit to width, center vertically
      offsetX = 0
      offsetY = (containerHeight - imgHeight) / 2
    } else {
      // Image is taller - fit to height, center horizontally
      offsetX = (containerWidth - imgWidth) / 2
      offsetY = 0
    }
    
    // Get the image container's position relative to the canvas
    const imageContainerRect = imageContainer.getBoundingClientRect()
    const canvasRect = canvas.getBoundingClientRect()
    
    // Calculate the image container's offset relative to the canvas
    const containerOffsetX = imageContainerRect.left - canvasRect.left
    const containerOffsetY = imageContainerRect.top - canvasRect.top
    
    // Scale to canvas coordinates
    const canvasScaleX = canvas.width / canvasRect.width
    const canvasScaleY = canvas.height / canvasRect.height
    
    const canvasContainerOffsetX = containerOffsetX * canvasScaleX
    const canvasContainerOffsetY = containerOffsetY * canvasScaleY
    const canvasOffsetX = canvasContainerOffsetX + (offsetX * canvasScaleX)
    const canvasOffsetY = canvasContainerOffsetY + (offsetY * canvasScaleY)
    const canvasImgWidth = imgWidth * canvasScaleX
    const canvasImgHeight = imgHeight * canvasScaleY
    
    canvas._imageBounds = {
      left: canvasOffsetX,
      top: canvasOffsetY,
      right: canvasOffsetX + canvasImgWidth,
      bottom: canvasOffsetY + canvasImgHeight,
      naturalWidth,
      naturalHeight,
      displayWidth: imgWidth,
      displayHeight: imgHeight
    }
  }

  if (!visible) return null

  const getPos = (e) => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    const client = e.touches ? e.touches[0] : e
    // More precise coordinate mapping with device pixel ratio
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return { 
      x: (client.clientX - rect.left) * scaleX, 
      y: (client.clientY - rect.top) * scaleY 
    }
  }

  const start = (e) => { 
    if (!isSliding) {
      setPainting(true); 
      draw(e);
    }
  }
  const end = () => {
    setPainting(false)
    // Save to history after each stroke completion
    setTimeout(() => {
      saveToHistory()
    }, 10) // Small delay to ensure drawing is complete
  }

  const clear = () => {
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    ctx.clearRect(0, 0, canvas.width, canvas.height)
    // Redraw the boundary after clearing
    drawImageBoundary()
    
    // Save to history after clearing
    setTimeout(() => {
      saveToHistory()
    }, 10)
  }

  // Save current canvas state to history
  const saveToHistory = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    
    const newCanvas = document.createElement('canvas')
    newCanvas.width = canvas.width
    newCanvas.height = canvas.height
    const newCtx = newCanvas.getContext('2d')
    newCtx.drawImage(canvas, 0, 0)
    
    // Remove any future history if we're not at the end
    const newHistory = maskHistory.slice(0, historyIndex + 1)
    newHistory.push(newCanvas)
    
    // Limit history to prevent memory issues (keep last 20 states)
    if (newHistory.length > 20) {
      newHistory.shift()
    }
    
    setMaskHistory(newHistory)
    setHistoryIndex(newHistory.length - 1)
    setCanUndo(newHistory.length > 1)
    setCanRedo(false)
  }

  // Undo function
  const undo = () => {
    console.log('🔄 Undo clicked - History index:', historyIndex, 'History length:', maskHistory.length)
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1
      console.log('🔄 Undoing to index:', newIndex)
      setHistoryIndex(newIndex)
      setCanUndo(newIndex > 0)
      setCanRedo(true)
      
      // Restore canvas state
      const canvas = canvasRef.current
      if (canvas && maskHistory[newIndex]) {
        const ctx = canvas.getContext('2d')
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.drawImage(maskHistory[newIndex], 0, 0)
        drawImageBoundary()
        console.log('✅ Undo completed')
      } else {
        console.error('❌ Canvas or history state not found for undo')
      }
    } else {
      console.log('❌ Cannot undo - already at beginning')
    }
  }

  // Redo function
  const redo = () => {
    if (historyIndex < maskHistory.length - 1) {
      const newIndex = historyIndex + 1
      setHistoryIndex(newIndex)
      setCanUndo(true)
      setCanRedo(newIndex < maskHistory.length - 1)
      
      // Restore canvas state
      const canvas = canvasRef.current
      const ctx = canvas.getContext('2d')
      ctx.clearRect(0, 0, canvas.width, canvas.height)
      ctx.drawImage(maskHistory[newIndex], 0, 0)
      drawImageBoundary()
    }
  }

  // Draw image boundary indicator on separate canvas
  const drawImageBoundary = () => {
    const boundaryCanvas = boundaryCanvasRef.current
    if (!boundaryCanvas || !canvasRef.current?._imageBounds) return
    
    const ctx = boundaryCanvas.getContext('2d')
    const bounds = canvasRef.current._imageBounds
    
    // Clear the boundary canvas
    ctx.clearRect(0, 0, boundaryCanvas.width, boundaryCanvas.height)
    
    // Draw a subtle border around the image area
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.6)'
    ctx.lineWidth = 1
    ctx.setLineDash([6, 3])
    ctx.strokeRect(bounds.left, bounds.top, bounds.right - bounds.left, bounds.bottom - bounds.top)
    
    // Add a very subtle background to show the drawing area
    ctx.fillStyle = 'rgba(59, 130, 246, 0.05)'
    ctx.fillRect(bounds.left, bounds.top, bounds.right - bounds.left, bounds.bottom - bounds.top)
  }

  // Redraw boundary when canvas is updated
  useEffect(() => {
    if (visible && canvasRef.current) {
      // Small delay to ensure canvas is ready
      const timer = setTimeout(() => {
        drawImageBoundary()
      }, 100)
      return () => clearTimeout(timer)
    }
  }, [visible, canvasRef.current?._imageBounds])

  // Redraw boundary after each draw operation
  const draw = (e) => {
    if (!painting) return
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')
    const { x, y } = getPos(e)

    // Get image bounds
    const bounds = canvas._imageBounds
    if (!bounds) return
    
    // Check if stroke center is within actual image bounds
    if (x < bounds.left || x > bounds.right || y < bounds.top || y > bounds.bottom) return
    
    // ChatGPT-style solid circle brush with blue color
    // Ensure brush size is consistent regardless of image scaling
    const radius = Math.max(1, brushSize / 2)
    ctx.globalCompositeOperation = 'source-over'
    ctx.beginPath()
    ctx.arc(x, y, radius, 0, Math.PI * 2)
    ctx.fillStyle = 'rgba(59, 130, 246, 0.15)'  // Very subtle like ChatGPT
    ctx.fill()
  }

  const exportMask = () => {
    const canvas = canvasRef.current
    const container = containerRef?.current
    if (!container) {
      console.error('❌ No container found')
      return
    }
    
    const img = container.querySelector('.main-image')
    if (!img) {
      console.error('❌ No image found')
      return
    }
    
    // Use stored image bounds to ensure we only export within image
    const bounds = canvas._imageBounds
    if (!bounds) {
      console.error('❌ No image bounds found')
      return
    }
    
    // Calculate the exact portion of the canvas that contains the image
    const sourceLeft = Math.max(0, Math.floor(bounds.left))
    const sourceTop = Math.max(0, Math.floor(bounds.top))
    const sourceRight = Math.min(canvas.width, Math.ceil(bounds.right))
    const sourceBottom = Math.min(canvas.height, Math.ceil(bounds.bottom))
    
    const sourceWidth = sourceRight - sourceLeft
    const sourceHeight = sourceBottom - sourceTop
    
    if (sourceWidth <= 0 || sourceHeight <= 0) {
      console.error('❌ Invalid source dimensions:', { sourceWidth, sourceHeight })
      return
    }
    
    try {
      // Get image data only from within the image bounds
      const imageData = canvas.getContext('2d').getImageData(sourceLeft, sourceTop, sourceWidth, sourceHeight)
      
      // Create mask canvas with EXACT same dimensions as the natural image
      const maskCanvas = document.createElement('canvas')
      maskCanvas.width = bounds.naturalWidth
      maskCanvas.height = bounds.naturalHeight
      const maskCtx = maskCanvas.getContext('2d')
      
      // Fill with black background (unmasked areas)
      maskCtx.fillStyle = 'black'
      maskCtx.fillRect(0, 0, maskCanvas.width, maskCanvas.height)
      
      // Scale the mask data to match natural image dimensions
      const scaledCanvas = document.createElement('canvas')
      scaledCanvas.width = bounds.naturalWidth
      scaledCanvas.height = bounds.naturalHeight
      const scaledCtx = scaledCanvas.getContext('2d')
      
      // Create a temporary canvas with the extracted image data
      const tempCanvas = document.createElement('canvas')
      tempCanvas.width = sourceWidth
      tempCanvas.height = sourceHeight
      const tempCtx = tempCanvas.getContext('2d')
      tempCtx.putImageData(imageData, 0, 0)
      
      // Scale the mask to natural image dimensions
      scaledCtx.drawImage(tempCanvas, 0, 0, sourceWidth, sourceHeight, 0, 0, bounds.naturalWidth, bounds.naturalHeight)
      
      // Get the scaled image data
      const scaledImageData = scaledCtx.getImageData(0, 0, bounds.naturalWidth, bounds.naturalHeight)
      const data = scaledImageData.data
      
      // Process pixels to convert blue mask to white - pixel-perfect mapping
      const processedData = new Uint8ClampedArray(data.length)
      
      for (let i = 0; i < data.length; i += 4) {
        const blue = data[i + 2]
        const alpha = data[i + 3]
        // If there's any blue content with alpha, make it white
        if (blue > 0 && alpha > 0) {
          processedData[i] = 255     // R
          processedData[i + 1] = 255 // G
          processedData[i + 2] = 255 // B
          processedData[i + 3] = 255 // A
        } else {
          processedData[i] = 0       // R
          processedData[i + 1] = 0   // G
          processedData[i + 2] = 0   // B
          processedData[i + 3] = 255 // A
        }
      }
      
      // Put processed data to mask canvas
      const processedImageData = new ImageData(processedData, bounds.naturalWidth, bounds.naturalHeight)
      maskCtx.putImageData(processedImageData, 0, 0)
      
      // Export binary mask
      maskCanvas.toBlob((maskBlob) => {
        if (!maskBlob) {
          console.error('❌ Failed to create mask blob')
          return
        }
        
        onApply?.(maskBlob)
        
        // Create debug alpha-blended version
        setTimeout(() => {
          try {
            createDebugBlend(img, maskCanvas, bounds)
          } catch (error) {
            console.error('❌ Error creating debug blend:', error)
          }
        }, 100)
      }, 'image/png')
      
    } catch (error) {
      console.error('Error extracting mask data:', error)
      return
    }
  }

  const createDebugBlend = (img, maskCanvas, bounds) => {
    // Create debug canvas with same dimensions as the natural image
    const debugCanvas = document.createElement('canvas')
    debugCanvas.width = bounds.naturalWidth
    debugCanvas.height = bounds.naturalHeight
    const debugCtx = debugCanvas.getContext('2d')
    
    // Draw the original image at full resolution
    debugCtx.drawImage(img, 0, 0, bounds.naturalWidth, bounds.naturalHeight)
    
    // Blend with mask (mask 0.3, image 0.7)
    debugCtx.globalAlpha = 0.3
    debugCtx.globalCompositeOperation = 'source-over'
    debugCtx.drawImage(maskCanvas, 0, 0)
    
    // Convert to blob for potential use (but don't download)
    debugCanvas.toBlob((debugBlob) => {
      if (!debugBlob) {
        console.error('❌ Failed to create debug blend blob')
        return
      }
      
      // Store the blend blob for potential use by the parent component
      // but don't automatically download it
      console.log('✅ Debug blend created successfully')
    }, 'image/png')
  }

  return (
    <div className="mask-overlay">
      <canvas
        ref={canvasRef}
        className="mask-canvas"
        onMouseDown={start}
        onMouseMove={draw}
        onMouseUp={end}
        onMouseLeave={end}
        onTouchStart={start}
        onTouchMove={draw}
        onTouchEnd={end}
      />
      <canvas
        ref={boundaryCanvasRef}
        className="boundary-canvas"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          pointerEvents: 'none',
          zIndex: 1
        }}
      />
      <div className="mask-toolbar top centered">
        <div className="tool active" title="Magic Brush - Draw within the highlighted area"><i className="fas fa-paint-brush"/></div>
        <label className="slider-label">SIZE<input 
          type="range" 
          min="4" 
          max="40" 
          value={brushSize} 
          onChange={(e)=>setBrushSize(parseInt(e.target.value))}
          onMouseDown={() => setIsSliding(true)}
          onMouseUp={() => setIsSliding(false)}
          onTouchStart={() => setIsSliding(true)}
          onTouchEnd={() => setIsSliding(false)}
        /></label>
        <button className="tool" onClick={clear} title="Clear all mask strokes"><i className="fas fa-eraser"/></button>
        <button className={`tool ${canUndo ? '' : 'disabled'}`} onClick={undo} title="Undo last stroke" disabled={!canUndo}><i className="fas fa-undo"/></button>
        <button className="apply" onClick={exportMask} title="Save binary mask (white = masked, black = unmasked)">SAVE</button>
        <button className="close" onClick={onClose} title="Close magic brush">✕</button>
      </div>
    </div>
  )
}

export default MaskOverlay


